import { Suspense, lazy, useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { Maximize2, Minimize2 } from 'lucide-react';
import { PageHero } from '@/components/layout/PageHero';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useFullscreen } from '@/hooks/useFullscreen';
import { useApproveSections, useExtractionProposal, useSaveSectionEdit } from '../hooks';
import { extractionSourceUrl } from '../api';
import type { ReviewSection } from '../types';
import { OriginalPdfPane } from './OriginalPdfPane';
import { ADMIN_ONLY, READ_ONLY_CLASS, SectionCard } from './SectionCard';
import { useIsAdmin, useMe } from '@/features/auth/hooks';
import { SectionReviewNotice, SectionReviewSkeleton } from './SectionReviewStates';
import { failedExtractionCopy } from '../failure-copy';

// react-pdf can't render a DOCX — route Word sources to the (code-split) docx-preview
// renderer so the reviewer still sees the original document on the left.
const DocxSourcePreview = lazy(() => import('../components/DocxSourcePreview'));

/**
 * Section review (the pivot): the original document (left) beside the doc broken into
 * whole SECTIONS (right), each a single Keep/Dismiss unit shown with its structure
 * intact. Flagged sections sort first and start expanded; clean sections collapse to
 * their heading. The kept sections' structured markdown becomes the product's spec.
 * The only tech-spec review (the field-by-field fact audit is retired).
 */
export function SectionReviewScreen({ productId }: { productId: number }) {
  const { data: proposal, isLoading, isFetching, timedOut, refetch } = useExtractionProposal(productId);
  // order -> decision (true keep / false dismiss). Absent = default: clean kept, flagged undecided.
  const [decisions, setDecisions] = useState<Record<number, boolean>>({});
  // order -> expanded. Absent = default: flagged open, clean collapsed.
  const [openMap, setOpenMap] = useState<Record<number, boolean>>({});
  // order -> the reviewer's edited markdown (flagged sections only). Absent = unedited.
  const [edits, setEdits] = useState<Record<number, string>>({});
  // Restore the edits saved on an earlier visit — without this, leaving the review before
  // approving lost every edit. Merged in whenever fresh saved edits arrive, not just once: the
  // cached copy shown first can predate them, and a save fired while leaving can land just
  // after a quick return (its success patches the cache, which lands here). Only sections not
  // already edited on this visit are filled in, so this visit's edits always win and nothing
  // on screen is ever removed.
  const savedEdits = proposal?.section_edits;
  const canRestore = proposal?.status === 'ready' && !isFetching;
  useEffect(() => {
    if (!canRestore || !savedEdits) return;
    setEdits((current) => {
      const missing = Object.entries(savedEdits).filter(([order]) => !(Number(order) in current));
      if (missing.length === 0) return current; // nothing new — no re-render
      return { ...current, ...Object.fromEntries(missing.map(([order, md]) => [Number(order), md])) };
    });
  }, [canRestore, savedEdits]);
  const saveEdit = useSaveSectionEdit(productId);

  const sections = proposal?.sections ?? [];
  const keptOf = (s: ReviewSection): boolean | undefined =>
    s.order in decisions ? decisions[s.order] : s.flagged ? undefined : true;
  const openOf = (s: ReviewSection): boolean => (s.order in openMap ? openMap[s.order]! : s.flagged);
  // Fired when an edit is committed (Done / click-away / leaving the page mid-edit): keep it
  // on the page AND save it to the server so it survives leaving before approving.
  const onEdit = (order: number, markdown: string) => {
    setEdits((p) => ({ ...p, [order]: markdown }));
    saveEdit.mutate({ order, markdown }); // failures are reported by the hook
  };

  const decide = (order: number, keep: boolean) =>
    setDecisions((p) => ({ ...p, [order]: keep }));
  const toggle = (order: number) => {
    const s = sections.find((x) => x.order === order);
    const cur = order in openMap ? openMap[order]! : (s?.flagged ?? false);
    setOpenMap((p) => ({ ...p, [order]: !cur }));
  };
  // Bulk-decide the FLAGGED sections in one click (the clean ones are already kept by
  // default, so they need no bulk action). The reviewer eyeballs the flagged group at the
  // top, then keeps or dismisses them all at once.
  const setAllFlagged = (keep: boolean) =>
    setDecisions((p) => {
      const next = { ...p };
      for (const s of sections) if (s.flagged) next[s.order] = keep;
      return next;
    });

  const undecided = sections.filter((s) => keptOf(s) === undefined).length;
  const willSave = sections.filter((s) => keptOf(s) === true).length;
  const flaggedCount = sections.filter((s) => s.flagged).length;

  // Scroll guard: the full spec can't be approved unseen — Approve stays disabled until the
  // section list has been scrolled to its end (or it already fits without scrolling).
  const [seenAll, setSeenAll] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);
  const handleListScroll = (e: React.UIEvent<HTMLUListElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= 24) setSeenAll(true);
  };
  // If the list already fits (short doc), mark it seen so approve isn't trapped. Measured in
  // a rAF so the flex layout has clamped the list height before we read it.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      const e = listRef.current;
      if (e && e.scrollHeight - e.clientHeight <= 4) setSeenAll(true);
    });
    return () => cancelAnimationFrame(id);
  }, [sections.length]);
  // Also require at least one KEPT section: approving with everything dismissed would save an
  // empty spec, which silently falls the agents back to the raw source doc (see approve_sections).
  // Only company admins may approve (the server enforces it too); everyone else gets a
  // read-only review instead of a button that can only fail.
  const isAdmin = useIsAdmin();
  // false until the signed-in user has loaded — so the controls stay locked meanwhile, but the
  // "admins only" note waits for a real answer instead of flashing at admins.
  const roleKnown = useMe().isSuccess;
  const canApprove = isAdmin && undecided === 0 && seenAll && willSave > 0;

  const navigate = useNavigate();
  const { expanded, toggle: toggleFullscreen } = useFullscreen();
  const approve = useApproveSections(productId);
  const onApprove = async () => {
    if (!canApprove) return; // guard: mirrors the disabled button
    const keptOrders = sections.filter((s) => keptOf(s) === true).map((s) => s.order);
    const keptSet = new Set(keptOrders);
    const editList = Object.entries(edits)
      .map(([order, markdown]) => ({ order: Number(order), markdown }))
      .filter((e) => keptSet.has(e.order)); // only kept sections' edits are saved
    try {
      const { section_count } = await approve.mutateAsync({ keptOrders, edits: editList });
      toast.success(`Saved ${section_count} section${section_count === 1 ? '' : 's'}.`);
      navigate({ to: '/product-account/$productId/edit', params: { productId } });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save. Please try again.');
    }
  };

  // Non-review states — so an in-progress
  // extraction shows the lazy skeleton, never the empty "no read-back text" screen.
  // The terminal empty-text notice is reached ONLY once the build is done (ready)
  // but genuinely has no readable text (e.g. an unreadable scan even with OCR on).
  const status = proposal?.status;
  let notice: ReactNode = null;
  if (timedOut && status === 'pending') {
    notice = (
      <SectionReviewNotice
        title="This is taking longer than usual"
        body="Reading this document is still in progress or may have stalled. You can check again in a moment, or come back to it later from your products list."
        action={
          <Button variant="outline" size="sm" className="mt-1" onClick={() => void refetch()}>
            Check again
          </Button>
        }
      />
    );
  } else if (isLoading || !proposal || status === 'pending') {
    notice = <SectionReviewSkeleton />;
  } else if (status === 'failed') {
    const copy = failedExtractionCopy(proposal.failure_reason);
    notice = <SectionReviewNotice title={copy.title} body={copy.body} />;
  } else if (status === 'awaiting_upload') {
    notice = (
      <SectionReviewNotice
        title="This upload didn&rsquo;t finish"
        body="The document upload wasn&rsquo;t completed, so there&rsquo;s nothing to review yet. Upload the document again from your products list."
      />
    );
  } else if (status === 'approved') {
    // Already approved (here, or field by field before the section review existed). Reopening
    // it as a live review would only end in a refused second approval, losing any decisions.
    notice = (
      <SectionReviewNotice
        title="This document has already been reviewed"
        body="There&rsquo;s nothing left to review here."
        action={
          <Button
            variant="outline"
            size="sm"
            className="mt-1"
            onClick={() => navigate({ to: '/product-account/$productId/edit', params: { productId } })}
          >
            Open the product
          </Button>
        }
      />
    );
  } else if (!sections.length) {
    notice = (
      <SectionReviewNotice
        title="We couldn&rsquo;t read any text from this document"
        body="This looks like a scan or image with no readable text. Try re-saving it as a text-based PDF and uploading again."
      />
    );
  }

  if (notice) {
    return (
      <>
        <PageHero
          title="Review tech spec"
          subtitle="Your document is on the left. On the right it&rsquo;s split into sections &mdash; check each against the document and keep it, or dismiss it. Anything we weren&rsquo;t sure about is flagged and comes first."
        />
        <main className="mx-auto max-w-[1280px] px-4 pt-6 pb-8 sm:px-6 lg:px-10">{notice}</main>
      </>
    );
  }

  return (
    <>
      <PageHero
        title="Review tech spec"
        subtitle="Your document is on the left. On the right it&rsquo;s split into sections &mdash; check each against the document and keep it, or dismiss it. Anything we weren&rsquo;t sure about is flagged and comes first."
      />
      <main className="mx-auto max-w-[1280px] px-4 pt-6 pb-8 sm:px-6 lg:px-10">
        <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="text-[13px] text-muted">
            {undecided > 0 ? (
              <span className="font-semibold text-state-warning-text">{undecided} need a look</span>
            ) : (
              <span className="font-semibold text-state-success-text">Nothing left to review</span>
            )}
            <span> &middot; {willSave} of {sections.length} will be saved</span>
          </span>
          {flaggedCount > 0 && (
            <span className="flex items-center gap-1.5 text-[12px]">
              <span className="text-muted">Flagged:</span>
              <button
                type="button"
                disabled={!isAdmin}
                title={isAdmin ? undefined : ADMIN_ONLY}
                onClick={() => setAllFlagged(true)}
                className={cn(
                  'rounded border border-state-success-accent px-2 py-0.5 text-[11.5px] font-semibold text-state-success-text',
                  isAdmin ? 'hover:bg-state-success-bg' : READ_ONLY_CLASS,
                )}
              >
                Keep all
              </button>
              <button
                type="button"
                disabled={!isAdmin}
                title={isAdmin ? undefined : ADMIN_ONLY}
                onClick={() => setAllFlagged(false)}
                className={cn(
                  'rounded border border-line px-2 py-0.5 text-[11.5px] font-semibold text-muted',
                  isAdmin ? 'hover:border-navy hover:text-navy' : READ_ONLY_CLASS,
                )}
              >
                Dismiss all
              </button>
            </span>
          )}
          <span className="flex-1" />
          {!isAdmin ? (
            roleKnown && (
              <span className="text-[12.5px] text-muted">Only company admins can review and approve this spec.</span>
            )
          ) : (
            <>
              {undecided === 0 && !seenAll && (
                <span className="text-[12.5px] text-muted">Scroll through all sections to enable approve</span>
              )}
              {undecided === 0 && seenAll && willSave === 0 && (
                <span className="text-[12.5px] text-state-warning-text">Keep at least one section to approve</span>
              )}
            </>
          )}
          <Button
            type="button"
            variant="brand"
            size="sm"
            disabled={!canApprove || approve.isPending}
            title={
              !isAdmin
                ? ADMIN_ONLY
                : undecided > 0
                ? 'Keep or dismiss every flagged section before saving.'
                : !seenAll
                  ? 'Scroll through all the sections before saving.'
                  : willSave === 0
                    ? 'Keep at least one section — an empty spec can’t be saved.'
                    : undefined
            }
            onClick={() => void onApprove()}
          >
            {approve.isPending ? 'Saving…' : 'Approve & save'}
          </Button>
        </div>

        <div className="grid h-[calc(100vh-20rem)] min-h-[460px] grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.12fr)_minmax(0,1fr)]">
          {(proposal?.source_filename ?? '').toLowerCase().endsWith('.pdf') ? (
            <OriginalPdfPane sourceUrl={extractionSourceUrl(productId)} />
          ) : (
            <Suspense
              fallback={
                <div className="h-full rounded-xl border border-line bg-[#eef1f5] p-4 text-[13px] text-muted">
                  Loading document&hellip;
                </div>
              }
            >
              {/* key: a fresh preview per document, so its zoom and page width are
                  measured from scratch, never under the previous document's zoom. */}
              <DocxSourcePreview
                key={productId}
                filename={proposal?.source_filename ?? null}
                fileUrl={extractionSourceUrl(productId)}
              />
            </Suspense>
          )}
          {/* Sections panel. "Full screen" gives dense sections (wide tables) the whole viewport
              while editing; Escape or the button brings it back into the split view. */}
          <div
            data-expanded={expanded || undefined}
            className={cn(
              'flex min-h-0 flex-col',
              expanded && 'fixed inset-0 z-50 bg-white px-4 py-3 sm:px-6',
            )}
          >
            <div className="mb-2 flex shrink-0 items-center justify-between gap-3">
              <span className="text-[12.5px] font-semibold text-muted">
                {expanded ? 'Sections — full screen' : 'Sections'}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-muted hover:text-navy"
                onClick={toggleFullscreen}
              >
                {expanded ? (
                  <>
                    <Minimize2 className="size-4" aria-hidden /> Exit full screen
                  </>
                ) : (
                  <>
                    <Maximize2 className="size-4" aria-hidden /> Full screen
                  </>
                )}
              </Button>
            </div>
            <ul
              ref={listRef}
              onScroll={handleListScroll}
              className={cn(
                'min-h-0 flex-1 space-y-3 overflow-y-auto pr-1',
                expanded && 'mx-auto w-full max-w-[1100px]',
              )}
            >
              {sections.map((s) => (
                <SectionCard
                  key={s.order}
                  section={s}
                  kept={keptOf(s)}
                  open={openOf(s)}
                  editedMarkdown={s.order in edits ? edits[s.order] : undefined}
                  onToggle={toggle}
                  onDecide={decide}
                  onEdit={onEdit}
                  readOnly={!isAdmin}
                />
              ))}
            </ul>
          </div>
        </div>
      </main>
    </>
  );
}
